'use client';

import { forwardRef, ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import styles from './Chip.module.css';
import Icon from '../primitives/Icon';

type ChipVariant = 'plain' | 'filter' | 'input';
type ChipTone =
  | 'neutral'
  | 'accentSoft'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'onMedia'
  | 'onMediaWarning'
  | 'onMediaInfo';
type ChipSize = 'xs' | 'sm' | 'md';

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ChipVariant;
  /** Tone applies to `plain` chips only. Filter/Input are always neutral grey. */
  tone?: ChipTone;
  size?: ChipSize;
  label: string;
  icon?: ReactNode;
  /** Trailing-edge node (e.g. a dropdown chevron when the chip triggers a menu). */
  trailing?: ReactNode;
  /** Selected state for `filter` (adds tick lead icon) and `input` (darkens). */
  selected?: boolean;
  /** Remove handler for `input` chips (renders the trailing ✕). */
  onRemove?: () => void;
}

const Chip = forwardRef<HTMLButtonElement | HTMLSpanElement, ChipProps>(({
  variant = 'plain',
  tone = 'neutral',
  size = 'md',
  label,
  icon,
  trailing,
  selected = false,
  onRemove,
  disabled = false,
  className = '',
  ...props
}, ref) => {
  const showCheck = variant === 'filter' && selected;
  const showRemove = variant === 'input' && !disabled;
  const iconPx = size === 'xs' ? 12 : size === 'sm' ? 14 : 16;

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.();
  };

  const classes = [
    styles.chip,
    styles[variant],
    variant === 'plain' ? styles[tone] : '',
    styles[size],
    selected ? styles.selected : '',
    disabled ? styles.disabled : '',
    className,
  ].filter(Boolean).join(' ');

  const content = (
    <>
      {showCheck && (
        <span className={styles.leadingIcon}>
          <Icon name="check" size={iconPx} />
        </span>
      )}
      {icon && !showCheck && <span className={styles.leadingIcon}>{icon}</span>}
      {label && <span className={styles.label}>{label}</span>}
      {trailing && <span className={styles.trailingIcon}>{trailing}</span>}
      {showRemove && (
        <span
          className={styles.removeBtn}
          onClick={handleRemove}
          role="button"
          tabIndex={0}
          aria-label={`Remove ${label}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onRemove?.();
            }
          }}
        >
          <Icon name="close" size={iconPx} />
        </span>
      )}
    </>
  );

  // `plain` chips are static tags — render a non-interactive <span> so they can
  // safely nest inside interactive containers (e.g. a clickable list row).
  if (variant === 'plain') {
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        className={classes}
        {...(props as HTMLAttributes<HTMLSpanElement>)}
      >
        {content}
      </span>
    );
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      className={classes}
      disabled={disabled}
      aria-pressed={variant === 'filter' ? selected : undefined}
      {...props}
    >
      {content}
    </button>
  );
});

Chip.displayName = 'Chip';
export default Chip;
